import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  InputAdornment,
  Alert,
  AlertTitle,
} from '@mui/material';
import ReactECharts from 'echarts-for-react';
import {
  MetricCard,
  PageHeader,
  SectionCard,
  LoadingState,
} from '@/components/shared';
import useBreakpoint from '@/hooks/useBreakpoint';
import { MonthlyPnLData, MonthlyPnLRow } from '@/services/financeClients';
import { DatePreset, DateFilter } from './FinanceXPnL.container';

type Props = {
  pnl: MonthlyPnLData;
  isLoading: boolean;
  savingsTargetPct: number;
  onSavingsTargetChange: (pct: number) => void;
  dateFilter: DateFilter;
  onPresetChange: (preset: DatePreset) => void;
  onCustomDateChange: (startDate: string, endDate: string) => void;
};

const fmt = (amount: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function fmtMonth(yyyyMm: string): string {
  const parts = yyyyMm.split('-');
  const mo = parseInt(parts[1], 10);
  return `${MONTH_LABELS[mo - 1]} '${parts[0].slice(2)}`;
}

function PnLRow({ row }: { row: MonthlyPnLRow }): JSX.Element {
  const isOnTarget = row.surplus >= 0;

  return (
    <tr style={{ borderBottom: '1px solid hsla(0,0%,0%,0.08)' }}>
      <td style={{ padding: '10px 12px', fontWeight: 500 }}>
        {fmtMonth(row.month)}
      </td>
      <td style={{ padding: '10px 12px', textAlign: 'right' }}>{fmt(row.revenue)}</td>
      <td style={{ padding: '10px 12px', textAlign: 'right' }}>{fmt(row.cogs)}</td>
      <td style={{ padding: '10px 12px', textAlign: 'right', color: row.grossProfit >= 0 ? '#10B981' : '#EF4444' }}>
        {fmt(row.grossProfit)}
      </td>
      <td style={{ padding: '10px 12px', textAlign: 'right', fontSize: 13 }}>
        {row.revenue > 0 ? `${row.grossMarginPct}%` : '—'}
      </td>
      <td style={{ padding: '10px 12px', textAlign: 'right' }}>{fmt(row.opex)}</td>
      <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 500, color: row.netIncome >= 0 ? '#10B981' : '#EF4444' }}>
        {fmt(row.netIncome)}
      </td>
      <td style={{ padding: '10px 12px', textAlign: 'right', color: '#F59E0B' }}>
        {fmt(row.savingsTarget)}
      </td>
      <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 500, color: isOnTarget ? '#10B981' : '#EF4444' }}>
        {fmt(row.surplus)}
      </td>
      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
        {row.revenue === 0 && <span style={{ color: '#D4D4D8' }}>—</span>}
        {row.revenue > 0 && isOnTarget && <CheckCircleOutlineIcon sx={{ color: '#10B981', fontSize: 18 }} />}
        {row.revenue > 0 && !isOnTarget && <ErrorOutlineIcon sx={{ color: '#EF4444', fontSize: 18 }} />}
      </td>
    </tr>
  );
}

const FinanceXPnLComponent = function ({
  pnl,
  isLoading,
  savingsTargetPct,
  onSavingsTargetChange,
  dateFilter,
  onPresetChange,
  onCustomDateChange,
}: Props): JSX.Element {
  const { isMobile } = useBreakpoint();

  if (isLoading) return <LoadingState message="Loading P&L report..." />;

  const { rows, ytdRevenue, ytdNetIncome, ytdSavingsTarget, ytdSurplus, avgMonthlySavingsRate, warnings } = pnl;

  // Only show historical months (not forecast) in the table
  const displayRows = rows.filter((r) => !r.isForecast);

  // --- Date filter actions (same pattern as Dashboard) ---
  const dateFilterActions = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      <TextField
        label="Savings %"
        type="number"
        size="small"
        value={savingsTargetPct}
        onChange={(e) => {
          if (e.target.value === '') { onSavingsTargetChange(0); return; }
          const val = parseInt(e.target.value, 10);
          if (!Number.isNaN(val) && val >= 0 && val <= 100) onSavingsTargetChange(val);
        }}
        InputProps={{
          endAdornment: <InputAdornment position="end">%</InputAdornment>,
        }}
        sx={{ width: 110 }}
      />
      <ToggleButtonGroup
        value={dateFilter.preset}
        exclusive
        onChange={(_, val) => val && onPresetChange(val)}
        size="small"
        sx={{
          '& .MuiToggleButton-root': {
            textTransform: 'none',
            fontSize: 13,
            px: 1.5,
            py: 0.5,
            borderColor: '#E4E4E7',
            '&.Mui-selected': {
              backgroundColor: '#054AD915',
              color: '#054AD9',
              borderColor: '#054AD9',
              '&:hover': { backgroundColor: '#054AD925' },
            },
          },
        }}
      >
        <ToggleButton value="this_month">This Month</ToggleButton>
        <ToggleButton value="last_month">Last Month</ToggleButton>
        <ToggleButton value="this_quarter">This Quarter</ToggleButton>
        <ToggleButton value="this_year">YTD</ToggleButton>
        <ToggleButton value="last_year">Last Year</ToggleButton>
        <ToggleButton value="custom">Custom</ToggleButton>
      </ToggleButtonGroup>
      {dateFilter.preset === 'custom' && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            type="date"
            size="small"
            value={dateFilter.startDate}
            onChange={(e) => onCustomDateChange(e.target.value, dateFilter.endDate)}
            sx={{ '& .MuiInputBase-input': { fontSize: 13, py: 0.75 } }}
          />
          <TextField
            type="date"
            size="small"
            value={dateFilter.endDate}
            onChange={(e) => onCustomDateChange(dateFilter.startDate, e.target.value)}
            sx={{ '& .MuiInputBase-input': { fontSize: 13, py: 0.75 } }}
          />
        </Box>
      )}
    </Box>
  );

  // Chart: Revenue vs Total Costs vs Savings Target
  const chartOption = {
    tooltip: {
      trigger: 'axis' as const,
      formatter: (params: any) =>
        params.map((p: any) => `${p.marker} ${p.seriesName}: ${fmt(p.value)}`).join('<br/>'),
    },
    legend: {
      data: ['Revenue', 'Total Costs', 'Net Income', `${savingsTargetPct}% Target`],
      bottom: 0,
      textStyle: { fontSize: 12, color: '#71717A' },
    },
    grid: { left: 65, right: 20, top: 20, bottom: 44 },
    xAxis: {
      type: 'category' as const,
      data: displayRows.map((r) => fmtMonth(r.month)),
      axisLabel: { fontSize: 11, color: '#71717A' },
      axisLine: { lineStyle: { color: '#E4E4E7' } },
    },
    yAxis: {
      type: 'value' as const,
      axisLabel: { fontSize: 11, color: '#71717A', formatter: (v: number) => fmt(v) },
      splitLine: { lineStyle: { color: '#F4F4F5' } },
    },
    series: [
      {
        name: 'Revenue',
        type: 'bar',
        data: displayRows.map((r) => r.revenue),
        itemStyle: { color: '#10B981', borderRadius: [4, 4, 0, 0] },
        barGap: '10%',
      },
      {
        name: 'Total Costs',
        type: 'bar',
        data: displayRows.map((r) => r.cogs + r.opex),
        itemStyle: { color: '#EF4444', borderRadius: [4, 4, 0, 0] },
      },
      {
        name: 'Net Income',
        type: 'line',
        data: displayRows.map((r) => r.netIncome),
        lineStyle: { color: '#054AD9', width: 2 },
        itemStyle: { color: '#054AD9' },
        symbol: 'circle',
        symbolSize: 6,
      },
      {
        name: `${savingsTargetPct}% Target`,
        type: 'line',
        data: displayRows.map((r) => r.savingsTarget),
        lineStyle: { color: '#F59E0B', width: 2, type: 'dashed' as const },
        itemStyle: { color: '#F59E0B' },
        symbol: 'none',
      },
    ],
  };

  // Gross Margin chart
  const marginChartOption = {
    tooltip: {
      trigger: 'axis' as const,
      formatter: (params: any) =>
        params.map((p: any) => `${p.marker} ${p.seriesName}: ${p.value}%`).join('<br/>'),
    },
    legend: {
      data: ['Gross Margin %'],
      bottom: 0,
      textStyle: { fontSize: 12, color: '#71717A' },
    },
    grid: { left: 50, right: 20, top: 20, bottom: 44 },
    xAxis: {
      type: 'category' as const,
      data: displayRows.map((r) => fmtMonth(r.month)),
      axisLabel: { fontSize: 11, color: '#71717A' },
      axisLine: { lineStyle: { color: '#E4E4E7' } },
    },
    yAxis: {
      type: 'value' as const,
      axisLabel: { fontSize: 11, color: '#71717A', formatter: (v: number) => `${v}%` },
      splitLine: { lineStyle: { color: '#F4F4F5' } },
      max: 100,
    },
    series: [
      {
        name: 'Gross Margin %',
        type: 'line',
        data: displayRows.map((r) => r.grossMarginPct),
        lineStyle: { color: '#8B5CF6', width: 2 },
        itemStyle: { color: '#8B5CF6' },
        areaStyle: { color: 'rgba(139, 92, 246, 0.08)' },
        symbol: 'circle',
        symbolSize: 6,
      },
    ],
  };

  return (
    <>
      <PageHeader title="Monthly P&L" actions={dateFilterActions} />

      {/* Warnings */}
      {warnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: '12px' }} icon={<WarningAmberIcon />}>
          <AlertTitle sx={{ fontWeight: 700 }}>Data Completeness</AlertTitle>
          {warnings.map((w) => (
            <Box key={w} sx={{ fontSize: 14 }}>{w}</Box>
          ))}
        </Alert>
      )}

      {/* KPI Cards */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
        gap: 2,
        mb: 3,
      }}>
        <MetricCard
          label="Revenue"
          value={fmt(ytdRevenue)}
          color="#10B981"
        />
        <MetricCard
          label="Net Income"
          value={fmt(ytdNetIncome)}
          color={ytdNetIncome >= 0 ? '#10B981' : '#EF4444'}
        />
        <MetricCard
          label={`${savingsTargetPct}% Target`}
          value={fmt(ytdSavingsTarget)}
          color="#F59E0B"
        />
        <MetricCard
          label={ytdSurplus >= 0 ? 'Surplus' : 'Deficit'}
          value={fmt(ytdSurplus)}
          color={ytdSurplus >= 0 ? '#10B981' : '#EF4444'}
        />
      </Box>

      {/* Savings Rate Indicator */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mb: 3,
        px: 2,
        py: 1.5,
        border: '2px solid #F4F4F5',
        borderRadius: '12px',
        backgroundColor: avgMonthlySavingsRate >= savingsTargetPct ? 'rgba(16, 185, 129, 0.06)' : 'rgba(239, 68, 68, 0.06)',
      }}>
        {avgMonthlySavingsRate >= savingsTargetPct
          ? <CheckCircleOutlineIcon sx={{ color: '#10B981', fontSize: 20 }} />
          : <ErrorOutlineIcon sx={{ color: '#EF4444', fontSize: 20 }} />
        }
        <Box sx={{ fontSize: 14, fontWeight: 500, color: '#09090B' }}>
          Average monthly savings rate: <strong>{avgMonthlySavingsRate}%</strong>
          {avgMonthlySavingsRate >= savingsTargetPct
            ? ` — You're saving above your ${savingsTargetPct}% target`
            : ` — Below your ${savingsTargetPct}% target`
          }
        </Box>
      </Box>

      {/* Charts */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr',
        gap: 2,
        mb: 3,
      }}>
        <SectionCard title="Revenue vs Costs">
          <ReactECharts option={chartOption} style={{ height: 320 }} />
        </SectionCard>
        <SectionCard title="Gross Margin Trend">
          <ReactECharts option={marginChartOption} style={{ height: 320 }} />
        </SectionCard>
      </Box>

      {/* Monthly P&L Table */}
      <SectionCard title="Monthly Breakdown">
        <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #F4F4F5' }}>
                {['Month', 'Revenue', 'COGS', 'Gross Profit', 'GM%', 'OpEx', 'Net Income', `${savingsTargetPct}% Target`, 'Surplus', ''].map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: '10px 12px',
                      textAlign: h === 'Month' ? 'left' : 'right',
                      fontWeight: 500,
                      fontSize: 12,
                      color: '#71717A',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayRows.map((row) => (
                <PnLRow key={row.month} row={row} />
              ))}
              {/* Total Row */}
              <tr style={{ borderTop: '2px solid #09090B', fontWeight: 700, backgroundColor: '#FAFAFA' }}>
                <td style={{ padding: '12px', fontWeight: 700 }}>Total</td>
                <td style={{ padding: '12px', textAlign: 'right' }}>{fmt(pnl.ytdRevenue)}</td>
                <td style={{ padding: '12px', textAlign: 'right' }}>{fmt(pnl.ytdCogs)}</td>
                <td style={{ padding: '12px', textAlign: 'right', color: pnl.ytdGrossProfit >= 0 ? '#10B981' : '#EF4444' }}>
                  {fmt(pnl.ytdGrossProfit)}
                </td>
                <td style={{ padding: '12px', textAlign: 'right' }}>
                  {ytdRevenue > 0 ? `${Math.round(pnl.ytdGrossProfit / ytdRevenue * 1000) / 10}%` : '—'}
                </td>
                <td style={{ padding: '12px', textAlign: 'right' }}>{fmt(pnl.ytdOpex)}</td>
                <td style={{ padding: '12px', textAlign: 'right', color: pnl.ytdNetIncome >= 0 ? '#10B981' : '#EF4444' }}>
                  {fmt(pnl.ytdNetIncome)}
                </td>
                <td style={{ padding: '12px', textAlign: 'right', color: '#F59E0B' }}>{fmt(pnl.ytdSavingsTarget)}</td>
                <td style={{ padding: '12px', textAlign: 'right', color: pnl.ytdSurplus >= 0 ? '#10B981' : '#EF4444' }}>
                  {fmt(pnl.ytdSurplus)}
                </td>
                <td style={{ padding: '12px', textAlign: 'center' }}>
                  {pnl.ytdSurplus >= 0
                    ? <CheckCircleOutlineIcon sx={{ color: '#10B981', fontSize: 18 }} />
                    : <ErrorOutlineIcon sx={{ color: '#EF4444', fontSize: 18 }} />
                  }
                </td>
              </tr>
            </tbody>
          </table>
        </Box>
      </SectionCard>
    </>
  );
};

export default FinanceXPnLComponent;
